import React, { useState } from 'react';
import { Clock, Info, Timer, CheckCircle2 } from 'lucide-react';
import {
  CartaoConfiguracoes as Card,
  CabecalhoCartaoConfiguracoes as CardHeader,
  TituloCartaoConfiguracoes as CardTitle,
  DescricaoCartaoConfiguracoes as CardDescription,
  ConteudoCartaoConfiguracoes as CardContent,
} from '@/components/settings/layout/CartaoConfiguracoes';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useClinicServices } from '@/hooks/useClinicServices';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

/**
 * O que faz: Seção de Configuração de Tempos de Procedimentos
 * Onde usa: AISettingsForm, AdminAISettings
 * 
 * - Exibe lista de todos os serviços cadastrados da clínica
 * - Permite configurar tempo médio em minutos para cada procedimento
 * - Valida entrada (5-480 minutos)
 * - Salva configurações individuais em tempo real
 * 
 * REFATORADO: Lista de serviços com visual premium e feedback de salvamento
 */

interface AIServiceTimingSectionProps {
  targetClinicaId?: string;
  readOnly?: boolean;
}

export const AIServiceTimingSection = ({
  targetClinicaId,
  readOnly = false
}: AIServiceTimingSectionProps) => {
  const {
    services,
    isLoading,
    updateServiceTiming,
    isUpdatingTiming
  } = useClinicServices();
  const [timingValues, setTimingValues] = useState<Record<string, string>>({});
  const [savedServices, setSavedServices] = useState<Set<string>>(new Set());

  // Estado de carregamento
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <Timer className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>
                Tempos de Procedimentos
              </CardTitle>
              <CardDescription>
                Configure o tempo médio de cada procedimento
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-muted flex items-center justify-center">
                <ThinkingOrbLoader state="working" size={20} />
              </div>
              <p className="text-sm text-muted-foreground">Carregando serviços...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Função para lidar com mudanças nos inputs de tempo
  const handleTimingChange = (serviceId: string, value: string) => {
    if (!readOnly) {
      setTimingValues(prev => ({
        ...prev,
        [serviceId]: value
      }));
      // Remove da lista de salvos quando edita
      setSavedServices(prev => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }
  };

  // Função para salvar tempo de um serviço específico
  const handleSaveTiming = async (serviceId: string) => {
    if (readOnly) return;
    const timeValue = timingValues[serviceId];
    if (!timeValue || timeValue.trim() === '') {
      await updateServiceTiming({
        serviceId,
        tempoMinutos: null
      });
      setSavedServices(prev => new Set(prev).add(serviceId));
      return;
    }
    const timeNumber = parseInt(timeValue);
    if (isNaN(timeNumber) || timeNumber < 5 || timeNumber > 480) {
      return;
    }
    await updateServiceTiming({
      serviceId,
      tempoMinutos: timeNumber
    });
    setSavedServices(prev => new Set(prev).add(serviceId));

    // Auto-remove do indicador de salvo após 2 segundos
    setTimeout(() => {
      setSavedServices(prev => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }, 2000);
  };

  // Função para obter valor atual do input
  const getCurrentValue = (service: any) => {
    if (timingValues[service.id] !== undefined) {
      return timingValues[service.id];
    }
    return service.tempo_medio_minutos?.toString() || '';
  };

  // Se não há serviços cadastrados
  if (!services || services.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
              <Timer className="h-5 w-5" />
            </div>
            <div>
              <CardTitle>
                Tempos de Procedimentos
              </CardTitle>
              <CardDescription>
                Configure o tempo médio de cada procedimento
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
              <Clock className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium mb-1">
              Nenhum serviço cadastrado
            </p>
            <p className="text-sm text-muted-foreground">
              Cadastre serviços no cartão "Novo serviço", acima, para configurar tempos
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <Timer className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>
              Tempos de Procedimentos
            </CardTitle>
            <CardDescription>
              Configure o tempo médio de cada procedimento
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Lista de serviços */}
        <div className="space-y-2">
          {services.map(service => (
            <div
              key={service.id}
              className={`flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 ${savedServices.has(service.id)
                  ? 'bg-accent border-border'
                  : 'bg-muted border-border hover:bg-muted'
                }`}
            >
              <div className="flex-1 min-w-0">
                <Label className="text-sm font-medium text-foreground truncate block">
                  {service.nome_servico}
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="5"
                  max="480"
                  placeholder="Ex: 30"
                  value={getCurrentValue(service)}
                  onChange={e => handleTimingChange(service.id, e.target.value)}
                  onBlur={() => handleSaveTiming(service.id)}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      handleSaveTiming(service.id);
                    }
                  }}
                  disabled={isUpdatingTiming || readOnly}
                  className="w-20 text-center rounded-xl h-10 bg-card border-border"
                />
                <span className="text-sm text-muted-foreground min-w-[40px]">
                  min
                </span>
                {savedServices.has(service.id) && (
                  <CheckCircle2 className="w-5 h-5 text-accent-foreground animate-in fade-in zoom-in duration-200" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Informações auxiliares */}
        <div className="p-4 rounded-xl bg-muted border border-info/30/50">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-lg bg-info-subtle flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-info-text" />
            </div>
            <div className="text-sm">
              <p className="text-info-text font-medium mb-2">
                Como funciona:
              </p>
              <ul className="text-info-text space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-info mt-1.5 flex-shrink-0" />
                  <span>A IA usa estes tempos para calcular horários disponíveis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-info mt-1.5 flex-shrink-0" />
                  <span>Valores aceitos: 5 a 480 minutos (5 min a 8 horas)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-info mt-1.5 flex-shrink-0" />
                  <span>Deixe em branco para procedimentos sem tempo fixo</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};